import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import useEntityStore from '../../../stores/entityStore';
import { API_BASE_URL } from '../../../api/config';
import { entityEventService } from '../../../services/entityEventService';
import { entityService } from '../../../services/entityService';

export const computeSessionCommand: SelfContainedCommand = {
  id: 'compute_session',
  description: `☁️ COMPUTE SESSION: Access cloud compute environments for running commands and processing data

**USAGE:**
Interact with ephemeral compute sessions for data processing, scripting, and general computation.

**⭐ CRITICAL FOR API WORKFLOWS:**
When the user mentions using an external API (e.g., "use our Mixpanel API", "access our Stripe data"):
1. **FIRST:** Search for the APIConnection using entity_find (e.g., searchQuery="mixpanel" entityType="APIConnection")
2. **THEN:** Read the connection using entity_get to get credentials from the skeleton
3. **FINALLY:** Start the compute session with credentials passed via env_vars
**DO NOT ask the user for credentials if an APIConnection exists!**

**ACTIONS:**

1. **start** - Create new compute session
   - Returns: session_id (reference to ComputeSession entity)
   - Creates entity visible in sidebar

2. **exec** - Execute ANY shell command in session
   - ✅ Run scripts: python3 /workspace/script.py
   - ✅ List files: ls -la /workspace
   - ✅ Read files: cat /workspace/data.txt
   - ✅ Install packages: pip install pandas
   - ✅ Chain commands: cd /workspace && python3 script.py
   - Returns: stdout, stderr, exit_code
   - ⚠️ DO NOT use echo/cat to write files - use write_file action instead

3. **write_file** - Write file to session filesystem
   - ✅ ALWAYS use this to create scripts, configs, data files
   - Handles encoding and special characters automatically
   - Much more reliable than shell redirection
   - Files appear at /workspace/{file_path} in container

4. **read_file** - Read file from session filesystem (OPTIONAL)
   - Get file contents as string
   - Alternative: Use exec with cat /workspace/filename

5. **list_files** - List files in session (OPTIONAL)
   - Returns structured JSON with file metadata
   - Alternative: Use exec with ls -la /workspace

6. **import** - Import file from session to River
   - Creates appropriate entity based on file type
   - File becomes part of the River project

7. **pause** - Pause session (keeps files, stops container)
   - Save state for later

8. **resume** - Resume paused session
   - Continue where you left off

9. **stop** - Stop and archive session
   - Keeps entity and history, removes container

**Design Philosophy**: 
- 🎯 **exec is your main tool** - Use it for running scripts, listing files, reading outputs, installing packages, etc.
- 📝 **write_file is for file creation only** - Shell file writing is unreliable (quotes, newlines break)
- 🔍 **list_files/read_file are optional** - You can use ls and cat via exec instead

**Examples of Using exec for Everything**:
\`\`\`json
// List files
{"action": "exec", "command": "ls -la /workspace"}

// Read a file  
{"action": "exec", "command": "cat /workspace/data.txt"}

// Check Python version
{"action": "exec", "command": "python3 --version"}

// Run a script you created with write_file
{"action": "exec", "command": "python3 /workspace/analyze.py"}

// Install and run in one command
{"action": "exec", "command": "pip install pandas && python3 /workspace/script.py"}
\`\`\`

**PASSING API CREDENTIALS:**

⭐ **CRITICAL:** When working with external APIs, you MUST fetch credentials from APIConnection entities first!

**WORKFLOW:**
1. **Search for the APIConnection** using space_get_context or entity_search
   - Example: "Find Mixpanel connection" → use space_get_context to list all connections
2. **Read the APIConnection** using doc_read_v2 or entity_get to get credentials
   - The skeleton will contain encrypted credentials and API details
3. **Pass credentials as env_vars** when starting the session
4. **Your script accesses them** via os.environ['VARIABLE_NAME']

**EXAMPLE - Complete Workflow:**

Step 1: Search for connection
\`\`\`json
{
  "action": "space_get_context",
  "include_entity_types": ["APIConnection"]
}
\`\`\`

Step 2: Read connection to get credentials
\`\`\`json
{
  "action": "doc_read_v2",
  "documentId": "apiconnection_mixpanel_main",
  "selector": {"all": true}
}
\`\`\`

Step 3: Start session with credentials from the connection
\`\`\`json
{
  "action": "start",
  "environment": "python",
  "purpose": "Process Mixpanel data",
  "env_vars": {
    "MIXPANEL_PROJECT_SECRET": "abc123...",  // From connection.skeleton.credentials
    "MIXPANEL_PROJECT_ID": "12345"  // From connection.skeleton.config
  }
}
\`\`\`

**DO NOT** ask the user for credentials if there's an APIConnection in the space!

**EXAMPLE WORKFLOW - With API Integration:**

\`\`\`json
// ⭐ STEP 0: Find the APIConnection (if user mentions an API)
{
  "action": "entity_find",
  "searchQuery": "mixpanel",
  "entityType": "APIConnection"
}
// Result: Found apiconnection_mixpanel_main

// ⭐ STEP 1: Read the connection to get credentials
{
  "action": "entity_get",
  "entityId": "apiconnection_mixpanel_main"
}
// Result: Entity with skeleton containing credentials, baseUrl, etc.

// STEP 2: Start session with credentials
{
  "action": "start",
  "environment": "python",
  "purpose": "Process Mixpanel data for DAU/MAU analysis",
  "env_vars": {
    "MIXPANEL_PROJECT_SECRET": "9c72b1d9b3ad...",  // From skeleton.credentials
    "MIXPANEL_PROJECT_ID": "12345",  // From skeleton.config
    "MIXPANEL_BASE_URL": "https://data.mixpanel.com"  // From skeleton.baseUrl
  }
}

// STEP 3: Install dependencies (if needed)
{
  "action": "exec",
  "session_id": "computesession_abc123",
  "command": "pip install pandas requests"
}

// STEP 4: ✅ CORRECT: Use write_file to create script
{
  "action": "write_file",
  "session_id": "computesession_abc123",
  "file_path": "analyze.py",
  "content": "import pandas as pd\nimport requests\nimport os\n\n# Credentials from environment\napi_secret = os.environ['MIXPANEL_PROJECT_SECRET']\nproject_id = os.environ['MIXPANEL_PROJECT_ID']\nbase_url = os.environ['MIXPANEL_BASE_URL']\n\nprint('Connected to Mixpanel!')\n"
}

// STEP 5: Execute the script
{
  "action": "exec",
  "session_id": "computesession_abc123",
  "command": "python analyze.py"
}

// STEP 6: Import results
{
  "action": "import",
  "session_id": "computesession_abc123",
  "file_path": "results.csv",
  "import_as": "DataExport",
  "name": "Mixpanel DAU/MAU - January 2026"
}
\`\`\`

**IMPORTANT - FILE EXECUTION:**

✅ **CORRECT Way to Create and Run Scripts:**
1. Use write_file to create the script
2. Use exec with simple command to run it
3. NO heredocs, NO inline code, NO temp files

**CORRECT Example:**
\`\`\`json
// Step 1: Write the file
{
  "action": "write_file",
  "session_id": "computesession_abc123",
  "file_path": "analyze.py",
  "content": "import pandas as pd\nprint('Hello!')\n"
}

// Step 2: Run it with simple command
{
  "action": "exec",
  "session_id": "computesession_abc123",
  "command": "python3 /workspace/analyze.py"
}
\`\`\`

❌ **WRONG - Don't do these:**
\`\`\`bash
# ❌ DON'T use heredocs or inline Python
python3 << 'EOF'
import pandas as pd
EOF

# ❌ DON'T use python -c with multi-line code
python3 -c 'import pandas\nprint("hello")'

# ❌ DON'T create temp files in /tmp
echo 'code' > /tmp/script.py && python3 /tmp/script.py

# ❌ DON'T pipe code to python
echo 'import pandas' | python3

# ✅ CORRECT - Use write_file then simple exec
# (See correct example above)
\`\`\`

**KEY RULES:**
- ✅ USE write_file action to create Python scripts, configs, data files
- ✅ USE simple "python3 /workspace/filename.py" commands
- ❌ DON'T use shell commands like echo, cat, or printf to write files
- ❌ DON'T use heredocs, python -c, or stdin piping
- write_file handles encoding, quotes, and special characters correctly
- Files are mounted at /workspace inside the container

**NOTES:**
- Sessions are entities - they show up in your sidebar
- Files persist until session is stopped
- Can return to paused sessions anytime
- Any file type can be imported to River`,

  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  examples: {
    good: [
      {
        input: {
          action: 'start',
          environment: 'python',
          purpose: 'Process Mixpanel data for DAU/MAU analysis'
        },
        expectedOutput: {
          status: 'success',
          message: 'Started compute session',
          format: 'json',
          data: {
            session_id: 'computesession_abc123',
            status: 'active'
          }
        },
        description: 'Start a Python compute session for data processing'
      },
      {
        input: {
          action: 'exec',
          session_id: 'computesession_abc123',
          command: 'pip install pandas requests'
        },
        expectedOutput: {
          status: 'success',
          message: 'Command executed',
          format: 'json',
          data: {
            stdout: 'Successfully installed pandas-2.2.0...',
            exit_code: 0
          }
        },
        description: 'Install Python packages in compute session'
      }
    ],
    bad: [
      {
        input: {
          action: 'exec',
          command: 'python script.py'
        },
        error: 'session_id is required',
        why: 'All actions except "start" require a session_id',
        fix: 'Include session_id parameter with the entity ID of an active ComputeSession'
      }
    ]
  },
  
  schema: {
    parameters: [
      {
        name: 'action',
        type: 'string',
        description: 'Action to perform: start|exec|write_file|read_file|list_files|import|pause|resume|stop',
        required: true,
      },
      {
        name: 'session_id',
        type: 'string',
        description: 'ComputeSession entity ID (not needed for "start" action)',
        required: false,
      },
      {
        name: 'environment',
        type: 'string',
        description: 'Environment preset for "start": python|node|general (default: general)',
        required: false,
      },
      {
        name: 'purpose',
        type: 'string',
        description: 'Natural language description of session purpose (for "start")',
        required: false,
      },
      {
        name: 'env_vars',
        type: 'object',
        description: 'Environment variables to set in session (for "start" or "exec"). Use for API credentials, config values, etc.',
        required: false,
      },
      {
        name: 'command',
        type: 'string',
        description: 'Shell command to execute (for "exec")',
        required: false,
      },
      {
        name: 'file_path',
        type: 'string',
        description: 'Path to file in session (for read/write/import)',
        required: false,
      },
      {
        name: 'content',
        type: 'string',
        description: 'File content (for "write_file")',
        required: false,
      },
      {
        name: 'import_as',
        type: 'string',
        description: 'Entity type to create when importing: auto|DataExport|Script|Note|Image|File',
        required: false,
      },
      {
        name: 'name',
        type: 'string',
        description: 'Human-readable name for imported entity',
        required: false,
      },
    ],
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { 
      action, 
      session_id, 
      environment, 
      purpose, 
      env_vars,
      command, 
      file_path, 
      content, 
      import_as, 
      name 
    } = params;
    
    console.log('[compute_session] 🖥️ Executing action:', JSON.stringify({
      action,
      session_id,
      environment,
      purpose,
      has_env_vars: !!env_vars,
      env_vars_count: env_vars ? Object.keys(env_vars).length : 0,
      command: command?.substring(0, 100),
      file_path,
      import_as,
      name,
      spaceId: context.spaceId
    }, null, 2));
    
    // Validate action
    const validActions = ['start', 'exec', 'write_file', 'read_file', 'list_files', 'import', 'pause', 'resume', 'stop'];
    if (!validActions.includes(action)) {
      return createErrorResult(
        `Invalid action: ${action}`,
        `Valid actions are: ${validActions.join(', ')}`,
        'validation'
      );
    }
    
    // Validate session_id for non-start actions
    if (action !== 'start' && !session_id) {
      return createErrorResult(
        'session_id is required',
        `session_id must be provided for action: ${action}`,
        'validation'
      );
    }
    
    // Validate action-specific parameters
    if (action === 'exec' && !command) {
      return createErrorResult(
        'command is required for exec action',
        'Provide the shell command to execute',
        'validation'
      );
    }
    
    if (action === 'write_file' && (!file_path || content === undefined)) {
      return createErrorResult(
        'file_path and content are required for write_file action',
        'Provide both file_path and content parameters',
        'validation'
      );
    }
    
    if ((action === 'read_file' || action === 'import') && !file_path) {
      return createErrorResult(
        'file_path is required',
        `file_path must be provided for action: ${action}`,
        'validation'
      );
    }
    
    try {
      // Build request payload
      const requestPayload: any = {
        action,
        session_id,
        environment: environment || 'general',
        purpose,
        env_vars,
        command,
        file_path,
        content,
        import_as: import_as || 'auto',
        name,
        space_id: context.spaceId,
      };
      
      console.log('[compute_session] 📤 Sending request to backend:', JSON.stringify({
        endpoint: `${API_BASE_URL}/compute-sessions/${action}`,
        payload: requestPayload
      }, null, 2));
      
      // Make API request (API_BASE_URL already includes /api)
      const response = await fetch(`${API_BASE_URL}/compute-sessions/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(requestPayload),
      });
      
      const result = await response.json();
      
      console.log('[compute_session] 📥 Received response:', JSON.stringify({
        status: response.status,
        success: result.success,
        message: result.message
      }, null, 2));
      
      if (!response.ok) {
        return createErrorResult(
          result.error || 'Compute session action failed',
          result.message || 'An error occurred while executing the compute session action',
          'execution'
        );
      }
      
      // If action is 'start', emit entity creation event to propagate across all stores
      if (action === 'start' && result.data?.session_id) {
        console.log('[compute_session] 🔄 Fetching newly created ComputeSession entity');
        
        // Fetch the created entity to get full entity data
        const entityStore = useEntityStore.getState();
        
        console.log('[compute_session] 📊 EntityStore state BEFORE fetch:', JSON.stringify({
          totalEntities: entityStore.entities.length,
          dataspaceRoomKey: entityStore.dataspaceRoomKey,
          storeStatus: entityStore.status,
          lifecycle: entityStore.lifecycle,
          initialized: entityStore.initialized,
          looking_for_session_id: result.data.session_id,
          looking_for_llmId: result.data.llmId,
          result_data: result.data
        }, null, 2));
        
        // ✅ CRITICAL FIX: Fetch the specific entity directly instead of refreshing the entire list
        // The list endpoint might not have the newly created entity yet due to eventual consistency
        let newSession = null;
        
        try {
          console.log('[compute_session] 📡 Fetching newly created entity directly from API:', JSON.stringify({
            session_id: result.data.session_id,
            llmId: result.data.llmId
          }, null, 2));
          
          // Try fetching by _id first (most reliable)
          newSession = await entityService.get(result.data.session_id);
          
          console.log('[compute_session] ✅ Fetched entity from API:', JSON.stringify({
            entityId: newSession._id,
            entityLlmId: newSession.llmId,
            entityType: newSession.entityType,
            entityName: newSession.name,
            entitySpaceId: newSession.spaceId
          }, null, 2));
        } catch (fetchError: any) {
          console.error('[compute_session] ❌ Direct fetch failed:', JSON.stringify({
            error: fetchError.message,
            session_id: result.data.session_id,
            willTryListRefresh: true
          }, null, 2));
          
          // Fallback: Try refreshing the entire list (in case direct fetch fails)
          await entityStore.fetchEntities();
          
          console.log('[compute_session] 📊 EntityStore state AFTER fetchEntities fallback:', JSON.stringify({
            totalEntities: entityStore.entities.length,
            dataspaceRoomKey: entityStore.dataspaceRoomKey,
            computeSessionCount: entityStore.entities.filter(e => e.entityType === 'ComputeSession').length
          }, null, 2));
          
          // Try finding in the refreshed list
          newSession = entityStore.entities.find(
            e => e.llmId === result.data.llmId || 
                 e._id === result.data.session_id ||
                 e.llmId === result.data.session_id
          );
        }
        
        if (newSession) {
          console.log('[compute_session] ✅ Found ComputeSession entity, emitting entityCreated event:', JSON.stringify({
            id: newSession._id,
            llmId: newSession.llmId,
            name: newSession.name,
            entityType: newSession.entityType,
            source: 'direct_fetch'
          }, null, 2));
          
          // Emit entity created event to propagate across all stores
          // This ensures dataspaceTreeStore automatically refreshes edges
          entityEventService.entityCreated(
            newSession,
            context.spaceId,
            {
              command: 'compute_session',
              userId: context.userId
            }
          );
        } else {
          console.warn('[compute_session] ⚠️ Could not find newly created ComputeSession entity:', JSON.stringify({
            session_id: result.data.session_id,
            result_data_llmId: result.data.llmId,
            totalEntities: entityStore.entities.length,
            computeSessions: entityStore.entities.filter(e => e.entityType === 'ComputeSession').map(e => ({
              _id: e._id,
              llmId: e.llmId,
              name: e.name,
              spaceId: e.spaceId
            })),
            allEntityTypesInStore: Array.from(new Set(entityStore.entities.map(e => e.entityType)))
          }, null, 2));
        }
      }
      
      // If action is 'import', emit entity creation event for the imported entity
      if (action === 'import' && result.data?.entity_id) {
        console.log('[compute_session] 🔄 Fetching imported entity after file import');
        
        // ✅ CRITICAL FIX: Fetch the specific entity directly instead of refreshing the entire list
        let importedEntity = null;
        
        try {
          console.log('[compute_session] 📡 Fetching imported entity directly from API:', JSON.stringify({
            entity_id: result.data.entity_id
          }, null, 2));
          
          importedEntity = await entityService.get(result.data.entity_id);
          
          console.log('[compute_session] ✅ Fetched imported entity from API:', JSON.stringify({
            entityId: importedEntity._id,
            entityLlmId: importedEntity.llmId,
            entityType: importedEntity.entityType,
            entityName: importedEntity.name
          }, null, 2));
        } catch (fetchError: any) {
          console.error('[compute_session] ❌ Direct fetch of imported entity failed:', JSON.stringify({
            error: fetchError.message,
            entity_id: result.data.entity_id,
            willTryListRefresh: true
          }, null, 2));
          
          // Fallback: Try refreshing the entire list
          const entityStore = useEntityStore.getState();
          await entityStore.fetchEntities();
          
          importedEntity = entityStore.entities.find(
            e => e.llmId === result.data.entity_id || e._id === result.data.entity_id
          );
        }
        
        if (importedEntity) {
          console.log('[compute_session] ✅ Found imported entity, emitting entityCreated event:', JSON.stringify({
            id: importedEntity._id,
            llmId: importedEntity.llmId,
            name: importedEntity.name,
            entityType: importedEntity.entityType
          }, null, 2));
          
          // Emit entity created event to propagate across all stores
          entityEventService.entityCreated(
            importedEntity,
            context.spaceId,
            {
              command: 'compute_session',
              userId: context.userId
            }
          );
        } else {
          console.warn('[compute_session] ⚠️ Could not find imported entity:', JSON.stringify({
            entity_id: result.data.entity_id
          }, null, 2));
        }
      }
      
      return createSuccessResult(
        result.message || 'Action completed successfully',
        result.data || {},
        { 
          metadata: result.metadata,
          format: 'json'
        }
      );
      
    } catch (error: any) {
      console.error('[compute_session] ❌ Error:', error);
      
      return createErrorResult(
        'Compute session request failed',
        error.message || 'An unexpected error occurred',
        'system'
      );
    }
  },
};
